import type {Extension} from '@codemirror/state'
import {type Command, type KeyBinding, keymap} from '@codemirror/view'

import type {HybridEditorTelemetry} from '../../telemetry'
import {formatBlockquote} from '../commands/blockquotes/format-blockquote'
import {splitBlockquoteIfBlankLine} from '../commands/blockquotes/split-blockquote-if-blank'
import {closeCodeBlock} from '../commands/code-blocks/close-code-block'
import {indentLineIfCodeBlock, unindentLineIfCodeBlock} from '../commands/code-blocks/indent-if-code-block'
import {formatHeading} from '../commands/headings/format-heading'
import {WRAP_DELIMITER_MAP, type WrappableDelimiter} from '../commands/inline-formatting/constants'
import {
  formatBold,
  formatInlineCode,
  formatItalic,
  formatStrikethrough,
} from '../commands/inline-formatting/format-inline'
import {formatLink} from '../commands/inline-formatting/format-link'
import {wrapWithDelimiter} from '../commands/inline-formatting/wrap-with-delimiter'
import {dedentListIfBlankItem} from '../commands/lists/dedent-if-blank'
import {dedentSelectedListItems} from '../commands/lists/dedent-selected-items'
import {formatOrderedList, formatUnorderedList} from '../commands/lists/format-list'
import {formatTaskList} from '../commands/lists/format-task-list'
import {indentSelectedListItems} from '../commands/lists/indent-selected-items'
import {insertNewListItem} from '../commands/lists/insert-new-item'
import {visitIfLink} from '../commands/visit-if-link'

export const formatLinkKeybinding: KeyBinding = {
  key: 'Mod-k',
  run: formatLink,
}

export const formatBoldKeybinding: KeyBinding = {
  key: 'Mod-b',
  run: formatBold,
}

export const formatItalicKeybinding: KeyBinding = {
  key: 'Mod-i',
  run: formatItalic,
}

export const formatInlineCodeKeybinding: KeyBinding = {
  key: 'Mod-e',
  run: formatInlineCode,
}

export const formatBlockquoteKeybinding: KeyBinding = {
  key: 'Mod-Shift-.',
  run: formatBlockquote,
}

export const formatUnorderedListKeybinding: KeyBinding = {
  key: 'Mod-Shift-8',
  run: formatUnorderedList,
}

export const formatOrderedListKeybinding: KeyBinding = {
  key: 'Mod-Shift-7',
  run: formatOrderedList,
}

export const formatHeading1Keybinding: KeyBinding = {key: 'Mod-Alt-1', run: view => formatHeading(view, 1)}
export const formatHeading2Keybinding: KeyBinding = {key: 'Mod-Alt-2', run: view => formatHeading(view, 2)}
export const formatHeading3Keybinding: KeyBinding = {key: 'Mod-Alt-3', run: view => formatHeading(view, 3)}
export const formatHeading4Keybinding: KeyBinding = {key: 'Mod-Alt-4', run: view => formatHeading(view, 4)}
export const formatHeading5Keybinding: KeyBinding = {key: 'Mod-Alt-5', run: view => formatHeading(view, 5)}
export const formatHeading6Keybinding: KeyBinding = {key: 'Mod-Alt-6', run: view => formatHeading(view, 6)}

export const formatStrikethroughKeybinding: KeyBinding = {
  key: 'Mod-Shift-x',
  run: formatStrikethrough,
}

export const formatTaskListKeybinding: KeyBinding = {
  key: 'Mod-Shift-l',
  run: formatTaskList,
}

const wrapWithDelimiterKeybindings: KeyBinding[] = Object.keys(WRAP_DELIMITER_MAP).map(key => ({
  key,
  run: wrapWithDelimiter(key as WrappableDelimiter),
}))

interface MarkdownKeymapExtensionOptions {
  primaryAction: Command
  trackKeyboardShortcut: HybridEditorTelemetry['trackKeyboardShortcut']
}

export function markdownKeymapExtension({
  primaryAction,
  trackKeyboardShortcut,
}: MarkdownKeymapExtensionOptions): Extension {
  const track = (binding: KeyBinding, commandId: string): KeyBinding => {
    if (!binding.run || !binding.key) return binding

    const run = binding.run
    const shortcut = binding.key

    return {
      ...binding,
      run: view => {
        const handled = run(view)
        trackKeyboardShortcut({commandId, shortcut, handled})
        return handled
      },
    }
  }

  const trackedBindings: Array<{binding: KeyBinding; commandId: string}> = [
    {binding: {key: 'Enter', run: closeCodeBlock}, commandId: 'close_code_block'},
    {binding: {key: 'Enter', run: dedentListIfBlankItem}, commandId: 'dedent_list_if_blank_item'},
    {binding: {key: 'Enter', run: insertNewListItem}, commandId: 'insert_new_list_item'},
    {binding: {key: 'Enter', run: splitBlockquoteIfBlankLine}, commandId: 'split_blockquote_if_blank_line'},
    {binding: {key: 'Alt-Enter', run: visitIfLink}, commandId: 'visit_if_link'},
    {binding: {key: 'Mod-Enter', run: primaryAction}, commandId: 'primary_action'},
    {binding: formatLinkKeybinding, commandId: 'format_link'},
    {binding: formatBoldKeybinding, commandId: 'format_bold'},
    {binding: formatItalicKeybinding, commandId: 'format_italic'},
    {binding: formatInlineCodeKeybinding, commandId: 'format_inline_code'},
    {binding: formatStrikethroughKeybinding, commandId: 'format_strikethrough'},
    {binding: formatTaskListKeybinding, commandId: 'format_task_list'},
    {binding: formatBlockquoteKeybinding, commandId: 'format_blockquote'},
    {binding: formatUnorderedListKeybinding, commandId: 'format_unordered_list'},
    {binding: formatOrderedListKeybinding, commandId: 'format_ordered_list'},
    {binding: formatHeading1Keybinding, commandId: 'format_heading_1'},
    {binding: formatHeading2Keybinding, commandId: 'format_heading_2'},
    {binding: formatHeading3Keybinding, commandId: 'format_heading_3'},
    {binding: formatHeading4Keybinding, commandId: 'format_heading_4'},
    {binding: formatHeading5Keybinding, commandId: 'format_heading_5'},
    {binding: formatHeading6Keybinding, commandId: 'format_heading_6'},
    ...wrapWithDelimiterKeybindings.map(binding => ({
      binding,
      commandId: binding.key
        ? `wrap_with_${binding.key.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase()}`
        : 'wrap_with_delimiter',
    })),
    {binding: {key: 'Tab', run: indentLineIfCodeBlock}, commandId: 'indent_line_if_code_block'},
    {binding: {key: 'Shift-Tab', run: unindentLineIfCodeBlock}, commandId: 'unindent_line_if_code_block'},
    {binding: {key: 'Shift-Tab', run: dedentSelectedListItems}, commandId: 'dedent_selected_list_items'},
    {binding: {key: 'Tab', run: indentSelectedListItems}, commandId: 'indent_selected_list_items'},
    {binding: {key: 'Mod-[', run: dedentSelectedListItems}, commandId: 'dedent_selected_list_items_mod'},
    {binding: {key: 'Mod-]', run: indentSelectedListItems}, commandId: 'indent_selected_list_items_mod'},
  ]

  const bindings: KeyBinding[] = trackedBindings.map(({binding, commandId}) => track({...binding}, commandId))

  return keymap.of(bindings)
}
